/**
 * Talent Sourcing sul telefono (7/10/2026): risponde a "chi stiamo formando e
 * chi possiamo collocare?". È una pipeline: corsi e candidati per fase, dal
 * primo contatto all'assunzione, più le richieste di body rental.
 *
 * Prima, a 390px: quattro numeri a zero in griglia ("Margine Talent 0.0%"),
 * due riquadri grandi per scegliere Academy o Body Rental, quattro linguette,
 * una frase dentro una scheda e un avviso "si fa dal computer" prima ancora
 * di sapere se c'era qualcosa.
 * Qui:
 *   1. in cima la risposta: quanti corsi sono in aula e quanti candidati
 *      sono pronti per un'azienda (o, se non c'è niente, lo si dice);
 *   2. le viste come pillole: Corsi, Candidati, Body rental, Docenti, Aziende
 *      (gli stessi parametri dell'indirizzo del computer: p e s);
 *   3. ogni vista raggruppata per fase, nell'ordine in cui le cose succedono;
 *      una riga per corso, persona o richiesta, e si apre la sua scheda.
 * Creare corsi, candidati e richieste resta al computer (sono moduli lunghi),
 * come prima.
 */
import { useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';
import { fmtEur } from '../../lib/utils';
import { DesktopOnly } from '../../components/ui/DesktopOnly';
import { MEmpty, MGroup, MHero, MPage, MRow, MSeg, iniziali, leggibile } from '../../components/mobile/MobileKit';
import { CandidateStage } from '@masamune/shared';
import './talent-mobile.css';

type Vista = 'corsi' | 'candidati' | 'bodyrental' | 'docenti' | 'aziende';

interface TalentStats {
  coursesActive: number; coursesFormatemp: number; coursesFondimpresa: number;
  studentsInClass: number; readyPlacement: number; recentReady: number;
  talentRevenue?: number; talentMarginPct?: number; placementYTD: number;
}
interface CourseItem {
  id: string; name: string; type: string; status: string; partnerName: string | null;
  client: { shortName: string } | null; startDate: string; endDate: string;
  hoursTotal: number; enrolledCount: number; revenue: string; cost: string;
}
interface CandidateItem {
  id: string; firstName: string; lastName: string; city: string | null; skill: string | null;
  seniority: string | null; stage: CandidateStage; availability: string | null;
  enrollments: Array<{ course: { name: string } }>;
}
interface BRRequest {
  id: string; title: string; status: string; neededRoles: number;
  client: { id: string; legalName: string; shortName: string };
  project: { id: string; name: string; code: string } | null;
  engagementCount: number; assignedCount: number;
  revenue: number; cost?: number; margin?: number; createdAt: string;
}
interface Teacher {
  kind: 'user' | 'supplier'; id: string; name: string; initials: string; email: string | null;
  city: string | null; hourlyRate: number | null;
  coursesCount: number; activeCoursesCount: number; hoursTotal: number; courseTypes: string[];
}
interface PartnerCompany {
  id: string; legalName: string; shortName: string; sector: string | null; city: string | null;
  activePlacements: number; totalPlacements: number;
  latestPlacement: { startDate: string | null; status: string; candidate: { firstName: string; lastName: string } } | null;
}

/** Le fasi di un corso, nell'ordine in cui succedono. */
export const FASE_CORSO: Array<[string, string]> = [
  ['ISCRIZIONI', 'Iscrizioni aperte'], ['SELEZIONE', 'In selezione'], ['AULA', 'In aula'], ['VALUTAZIONE', 'In valutazione'],
  ['PLACEMENT', 'In placement'], ['RENDICONTAZIONE', 'Da rendicontare'], ['CHIUSO', 'Chiusi'],
];
/** Le fasi di un candidato, dal primo contatto all'assunzione; gli scartati in fondo. */
export const FASE_CANDIDATO: Array<[string, string]> = [
  ['NUOVO_CANDIDATO', 'Nuovi'], ['SCREENING', 'In screening'], ['IDONEO', 'Idonei'], ['ISCRITTO_AL_CORSO', 'Iscritti a un corso'],
  ['FREQUENTANTE', 'Stanno frequentando'], ['PRONTO_PER_PLACEMENT', "Pronti per un'azienda"], ['PROPOSTO_AD_AZIENDA', "Proposti a un'azienda"],
  ['COLLOQUIO_FISSATO', 'Con un colloquio fissato'], ['SOMMINISTRATO', 'Somministrati'], ['ASSUNTO', 'Assunti'], ['SCARTATO', 'Scartati'],
];
/** Gli stati di una richiesta di body rental. */
export const FASE_BR: Array<[string, string]> = [
  ['RICHIESTA', 'Appena chieste'], ['RICERCA', 'Cerchiamo le figure'], ['PROPOSTO', 'Proposte al cliente'], ['ATTIVO', 'Ingaggi in corso'],
  ['TERMINATO', 'Concluse'], ['ANNULLATO', 'Annullate'],
];
const TIPO_CORSO: Record<string, string> = { FORMATEMP: 'Formatemp', FONDIMPRESA: 'Fondimpresa', INTERNO: 'Interno' };

const giorno = (iso: string) => {
  const d = new Date(iso);
  const stessoAnno = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString('it-IT', { day: 'numeric', month: 'short', ...(stessoAnno ? {} : { year: 'numeric' }) }).replace('.', '');
};

/** Raggruppa per fase nell'ordine dato; le fasi sconosciute finiscono in "Altri". */
function perFase<T>(items: T[], fase: (x: T) => string, ordine: Array<[string, string]>) {
  const gruppi = ordine.map(([k, l]) => ({ k, l, righe: items.filter((x) => fase(x) === k) })).filter((g) => g.righe.length);
  const altri = items.filter((x) => !ordine.some(([k]) => k === fase(x)));
  return altri.length ? [...gruppi, { k: 'ALTRO', l: 'Altri', righe: altri }] : gruppi;
}

export function TalentMobile() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const showMargin = !!user?.role.canSeeMargin;
  const [params, setParams] = useSearchParams();
  const pillar = params.get('p') === 'bodyrental' ? 'bodyrental' : 'academy';
  const section = params.get('s') || 'corsi';
  const vista: Vista = pillar === 'bodyrental' ? 'bodyrental'
    : section === 'pool' ? 'candidati' : section === 'docenti' ? 'docenti' : section === 'aziende' ? 'aziende' : 'corsi';

  // Stessi parametri del computer: p=academy|bodyrental, s=corsi|pool|docenti|aziende.
  const scegli = (v: Vista) => setParams((prev) => {
    const sp = new URLSearchParams(prev);
    for (const k of ['ordina', 'tipo', 'stato', 'fase', 'solo']) sp.delete(k);
    if (v === 'bodyrental') { sp.set('p', 'bodyrental'); sp.delete('s'); }
    else { sp.set('p', 'academy'); sp.set('s', v === 'candidati' ? 'pool' : v); }
    return sp;
  }, { replace: true });

  const { data: stats } = useQuery({ queryKey: ['talent-stats'], queryFn: () => api.get<TalentStats>('/talent/stats') });
  const corsiQ = useQuery({
    queryKey: ['courses', 'list', 'telefono'],
    queryFn: () => api.get<{ items: CourseItem[]; total: number }>('/courses?page=1&pageSize=100&sort=fase'),
    enabled: vista === 'corsi',
  });
  const candidatiQ = useQuery({
    queryKey: ['candidates', 'list', 'telefono'],
    queryFn: () => api.get<{ items: CandidateItem[]; total: number }>('/candidates?page=1&pageSize=100&sort=fase'),
    enabled: vista === 'candidati',
  });
  // Stessa chiave della lista del computer: arriva intera (fino a 100).
  const brQ = useQuery({
    queryKey: ['body-rental', 'list'],
    queryFn: () => api.get<{ items: BRRequest[] }>('/body-rental?pageSize=100'),
  });
  const docentiQ = useQuery({ queryKey: ['talent-teachers'], queryFn: () => api.get<Teacher[]>('/talent/teachers'), enabled: vista === 'docenti' });
  const aziendeQ = useQuery({ queryKey: ['talent-partner-companies'], queryFn: () => api.get<PartnerCompany[]>('/talent/partner-companies'), enabled: vista === 'aziende' });

  const richieste = brQ.data?.items ?? [];
  const brAperte = richieste.filter((r) => ['RICHIESTA', 'RICERCA', 'PROPOSTO', 'ATTIVO'].includes(r.status));

  // La risposta: con dei corsi in aula o dei candidati pronti, quelli; se
  // non c'è niente lo si dice a parole invece di quattro zeri.
  const hero = useMemo(() => {
    if (!stats) return <MHero label="Talent" value="…" />;
    if (vista === 'bodyrental') {
      const daCoprire = brAperte.reduce((a, r) => a + Math.max(0, r.engagementCount - r.assignedCount), 0);
      return brAperte.length
        ? <MHero label="Richieste di body rental aperte" value={brAperte.length} sub={daCoprire ? <span className="is-alert">{daCoprire === 1 ? '1 figura da assegnare' : `${daCoprire} figure da assegnare`}</span> : 'tutte le figure sono assegnate'} />
        : <MHero label="Body rental" value={<span className="tlm-frase">Nessuna richiesta aperta</span>} sub={richieste.length ? `${richieste.length} richieste concluse o annullate` : 'le richieste delle aziende arrivano qui'} />;
    }
    if (!stats.coursesActive && !stats.readyPlacement && !stats.studentsInClass) {
      return <MHero label="Academy" value={<span className="tlm-frase">Nessun corso in corso</span>} sub={stats.placementYTD ? `${stats.placementYTD} placement da inizio anno` : 'corsi, candidati e placement compaiono qui'} />;
    }
    return (
      <MHero
        label={stats.coursesActive === 1 ? 'Corso attivo' : 'Corsi attivi'}
        value={stats.coursesActive}
        sub={[
          stats.studentsInClass ? `${stats.studentsInClass} in aula` : null,
          stats.readyPlacement ? `${stats.readyPlacement} ${stats.readyPlacement === 1 ? 'pronto' : 'pronti'} per un'azienda` : null,
          showMargin && stats.talentMarginPct ? `margine ${stats.talentMarginPct.toFixed(0)}%` : null,
        ].filter(Boolean).join(' · ') || undefined}
      />
    );
  }, [stats, vista, brAperte, richieste.length, showMargin]);

  return (
    <div className="tlm">
      <MPage>
        {hero}

        <MSeg<Vista>
          value={vista}
          onChange={scegli}
          options={[
            // I conteggi a zero non si scrivono (mobile.md §12.3).
            { key: 'corsi', label: 'Corsi', count: stats?.coursesActive || undefined },
            { key: 'candidati', label: 'Candidati' },
            { key: 'bodyrental', label: 'Body rental', count: brAperte.length || undefined },
            { key: 'docenti', label: 'Docenti' },
            { key: 'aziende', label: 'Aziende partner' },
          ]}
        />

        {vista === 'corsi' && (
          <Elenco
            titolo="Corsi"
            caricando={corsiQ.isLoading && !corsiQ.data}
            vuoto="Nessun corso: i percorsi Formatemp e Fondimpresa compaiono qui, per fase."
            gruppi={perFase(corsiQ.data?.items ?? [], (c) => c.status, FASE_CORSO)}
            riga={(c: CourseItem) => (
              <MRow
                key={c.id}
                icon="talent"
                title={c.name}
                sub={[TIPO_CORSO[c.type] ?? c.type, c.partnerName ?? c.client?.shortName, `${giorno(c.startDate)} → ${giorno(c.endDate)}`].filter(Boolean).join(' · ')}
                trail={c.enrolledCount ? `${c.enrolledCount}` : undefined}
                trailSub={c.enrolledCount ? (c.enrolledCount === 1 ? 'iscritto' : 'iscritti') : undefined}
                onClick={() => navigate(`/courses/${c.id}`)}
              />
            )}
          />
        )}

        {vista === 'candidati' && (
          <Elenco
            titolo="Candidati"
            caricando={candidatiQ.isLoading && !candidatiQ.data}
            vuoto="Nessun candidato: il registro da cui si pescano i discenti dei corsi compare qui, fase per fase."
            gruppi={perFase(candidatiQ.data?.items ?? [], (c) => c.stage, FASE_CANDIDATO)}
            riga={(c: CandidateItem) => {
              const nome = `${c.firstName} ${c.lastName}`;
              return (
                <MRow
                  key={c.id}
                  text={iniziali(nome)}
                  round
                  title={nome}
                  sub={[c.skill, c.seniority, c.city ? leggibile(c.city) : null, c.enrollments[0]?.course.name].filter(Boolean).join(' · ') || undefined}
                  trail={c.availability ?? undefined}
                  trailTone="mute"
                  onClick={() => navigate(`/candidates/${c.id}`)}
                />
              );
            }}
          />
        )}

        {vista === 'bodyrental' && (
          <Elenco
            titolo="Richieste"
            caricando={brQ.isLoading && !brQ.data}
            vuoto="Nessuna richiesta di body rental: quando un'azienda chiede dei professionisti a margine, la richiesta compare qui."
            gruppi={perFase(richieste, (r) => r.status, FASE_BR)}
            riga={(r: BRRequest) => {
              const mancano = Math.max(0, r.engagementCount - r.assignedCount);
              return (
                <MRow
                  key={r.id}
                  icon="brief"
                  tone={mancano > 0 && ['RICHIESTA', 'RICERCA', 'PROPOSTO'].includes(r.status) ? 'alert' : undefined}
                  title={r.title}
                  sub={[leggibile(r.client.shortName || r.client.legalName), r.engagementCount ? `${r.assignedCount} su ${r.engagementCount} figure assegnate` : `${r.neededRoles} figure richieste`].join(' · ')}
                  trail={r.revenue ? `€ ${fmtEur(r.revenue)}` : undefined}
                  onClick={() => navigate(`/talent/body-rental/${r.id}`)}
                />
              );
            }}
          />
        )}

        {vista === 'docenti' && (
          <Elenco
            titolo="Docenti"
            caricando={docentiQ.isLoading && !docentiQ.data}
            vuoto="Nessun docente: compaiono qui quando vengono assegnati ai corsi."
            gruppi={[
              { k: 'attivi', l: 'Insegnano adesso', righe: (docentiQ.data ?? []).filter((t) => t.activeCoursesCount > 0) },
              { k: 'altri', l: 'Hanno insegnato', righe: (docentiQ.data ?? []).filter((t) => t.activeCoursesCount === 0) },
            ].filter((g) => g.righe.length)}
            riga={(t: Teacher) => (
              <MRow
                key={`${t.kind}:${t.id}`}
                text={t.initials || iniziali(t.name)}
                round
                title={t.name}
                sub={[t.kind === 'user' ? 'interno' : 'esterno', `${t.coursesCount} ${t.coursesCount === 1 ? 'corso' : 'corsi'}`, t.hoursTotal ? `${t.hoursTotal} h` : null, t.courseTypes.join(', ') || null].filter(Boolean).join(' · ')}
                trail={t.hourlyRate ? `€ ${t.hourlyRate}/h` : undefined}
                trailTone="mute"
              />
            )}
          />
        )}

        {vista === 'aziende' && (
          <Elenco
            titolo="Aziende partner"
            caricando={aziendeQ.isLoading && !aziendeQ.data}
            vuoto="Nessuna azienda partner: compaiono qui quando ospitano discenti in placement."
            gruppi={[
              { k: 'attive', l: 'Con un placement in corso', righe: (aziendeQ.data ?? []).filter((c) => Number(c.activePlacements) > 0) },
              { k: 'altre', l: 'Hanno ospitato', righe: (aziendeQ.data ?? []).filter((c) => !(Number(c.activePlacements) > 0)) },
            ].filter((g) => g.righe.length)}
            riga={(c: PartnerCompany) => (
              <MRow
                key={c.id}
                icon="building"
                title={leggibile(c.shortName || c.legalName)}
                sub={[c.sector, c.city ? leggibile(c.city) : null, c.latestPlacement ? `ultimo: ${c.latestPlacement.candidate.firstName} ${c.latestPlacement.candidate.lastName}` : null].filter(Boolean).join(' · ') || undefined}
                trail={`${c.totalPlacements}`}
                trailSub="placement"
                onClick={() => navigate(`/clients/${c.id}`)}
              />
            )}
          />
        )}

        <DesktopOnly
          cosa="Creare un corso, un candidato o una richiesta, o esportare i dati"
          perche="sono moduli lunghi (programma, profilo, condizioni economiche). Dal telefono si consulta e si aggiorna quello che c'è."
        ><></></DesktopOnly>
      </MPage>
    </div>
  );
}

function Elenco<T>({ titolo, caricando, vuoto, gruppi, riga }: {
  titolo: string;
  caricando: boolean;
  vuoto: string;
  gruppi: Array<{ k: string; l: string; righe: T[] }>;
  riga: (x: T) => ReactNode;
}) {
  if (caricando) return <MGroup title={titolo}><MEmpty>Carico…</MEmpty></MGroup>;
  if (!gruppi.length) return <MGroup title={titolo}><MEmpty>{vuoto}</MEmpty></MGroup>;
  return <>{gruppi.map((g) => <MGroup key={g.k} title={g.l} count={g.righe.length}>{g.righe.map(riga)}</MGroup>)}</>;
}
